import {Component, EventEmitter, Inject, Input, Output} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {TranslocoService} from '@ngneat/transloco';
import {environment} from "@environment";
import {AbstractComponent, AddUserModalComponent, AddUserResult, ConfirmationModalComponent} from "@components";
import {DEFAULT_PAGE_SIZE, FindUsersDetailsRequest, getRoleIdForRole, HELPER_ROLE, Organiser, ORGANIZER_ROLE, USER_ROLE, UserDetail, UsersDetailFilter} from "@model";
import {AdministratorsService, ExportService, LoaderService, OrganiserService, OrganiserUsersService, UserDetailService} from "@services";
import { DOCUMENT } from '@angular/common';
import { Router } from 'express';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-organiser-user-list',
  templateUrl: './organiser-user-list.component.html',
  styleUrls: ['./organiser-user-list.component.scss']
})
export class OrganiserUserListComponent extends AbstractComponent {
  @Input() users: UserDetail[] | undefined;
  @Output() onUsersChange: EventEmitter<UserDetail[] | undefined> = new EventEmitter<UserDetail[] | undefined>();

  currentUserDetails: UserDetail | undefined;
  currentOrganiser: Organiser | undefined;
  environment: any = environment;
  searchText: string = "";
  page: number = 1;
  selectedRole: number | undefined;
  queryParamsFirstChange: boolean = true;
  roles: number[] = UsersDetailFilter.ROLES;
  hasMoreUsers: boolean = true;
  isLoading: boolean = false;

  constructor(private userDetailsService: UserDetailService, private translocoService: TranslocoService,
              public dialog: MatDialog, private modalService: NgbModal, private loaderService: LoaderService,
              private organiserUserService: OrganiserUsersService, private organiserService: OrganiserService,private exportService: ExportService,
              @Inject(DOCUMENT) private document: Document,private route: ActivatedRoute, private adminService: AdministratorsService) {
    super();
  }

  ngOnInit(): void {
    this.subscriptions.push(this.userDetailsService.currentUserDetails.subscribe((userDetails: UserDetail | undefined) => {
      this.currentUserDetails = userDetails;
    }));
    this.subscribeToChangeOrganiser();
  }

  subsribeToRouteDataChange() {
    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay),
      distinctUntilChanged()).subscribe((queryParams: any) => {
        this.searchText = queryParams.search ? queryParams.search : "";
        this.selectedRole = this.getRoleValue(queryParams)
        if (!this.queryParamsFirstChange && this.isRoleValid(queryParams.role)) {
          this.onExplore(false);
        }
        this.queryParamsFirstChange = false;
      })
    );
  }

  private getRoleValue(queryParams: any) {
    if (queryParams.role) {
      return this.isRoleValid(queryParams.role) ? +queryParams.role : undefined
    }
    return undefined;
  }
  private isRoleValid(roleId: string | undefined) {
    if (!roleId) return true;
    else {
      return this.roles.includes(+roleId)
    }
  }

  onExplore(loadMore?: boolean) {
    if (loadMore !== true) {
      this.page = 1;
      this.hasMoreUsers = true;
    }
    if (this.hasMoreUsers) {
      let filter: FindUsersDetailsRequest = this.createFilter();
      this.adminService.findUsers(filter, !loadMore, () => {
        this.isLoading = loadMore ? this.isLoading : true;
      }, () => {
        setTimeout(() => {
          this.isLoading = loadMore ? this.isLoading : false;
        }, 700);
      }).subscribe((value: UserDetail[]) => {
        this.handleExploreResult(value, loadMore);
      }
      );
    }
  }
  handleExploreResult(users: UserDetail[], loadMore?: boolean) {
    if (users.length > DEFAULT_PAGE_SIZE) {
      this.hasMoreUsers = true;
      users = users.slice(0, DEFAULT_PAGE_SIZE);
    } else {
      this.hasMoreUsers = false;
    }
    if (loadMore && this.users) {
      users = this.users.concat(users);
    }
    this.setListOfItems(users);
  }
  setListOfItems(value: UserDetail[] | null) {
    if (value && value.length > 0) {
      this.users = value;
      this.page++;
    } else {
      this.users = [];
    }
  }

  private subscribeToChangeOrganiser() {
    this.subscriptions.push(this.organiserService.currentOrganiser.subscribe((value: Organiser | undefined) => {
      if (value) {
        this.currentOrganiser = value;
      }
    }));
  }

  openAddNewUserModal() {
    const modalRef: NgbModalRef = this.modalService.open(AddUserModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("organiser.list.add.new");
    modalRef.componentInstance.isAdmin = false;

    modalRef.result.then((result: AddUserResult) => {
      this.onAddUser(result.email!, result.roleId!);
    }, (_) => {
      /// ignore
    });
  }

  onAddUser(email: string, roleId: number): void {
    this.loaderService.startLoading();
    this.organiserUserService.addUser(email, roleId, this.currentOrganiser?.id).subscribe((_: boolean):void => {
      this.organiserUserService.findUsers(this.currentOrganiser?.id).subscribe((users: UserDetail[]) => {
        this.users = users;
        this.onUsersChange.emit(users);
        this.loaderService.dismissLoading();
      });
    })
  }

  openEditUserModal(user: UserDetail) {
    const modalRef: NgbModalRef = this.modalService.open(AddUserModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("organiser.list.edit.user");
    modalRef.componentInstance.isAdmin = false;
    modalRef.componentInstance.isEditUser = true;
    modalRef.componentInstance.email = user.email;
    modalRef.componentInstance.roleId = getRoleIdForRole(user.role);

    modalRef.result.then((result: AddUserResult) => {
      this.onEditUser(user.userId!, result.roleId!);
    }, (_) => {
      /// ignore
    });
  }

  onEditUser(userId: number, roleId: number): void {
    this.loaderService.startLoading();
    this.organiserUserService.editUser(userId, roleId, this.currentOrganiser!.id!).subscribe((_: boolean):void => {
      this.organiserUserService.findUsers(this.currentOrganiser?.id).subscribe((users: UserDetail[]) => {
        this.users = users;
        this.onUsersChange.emit(users);
        this.loaderService.dismissLoading();
      });
    })
  }

  openDeleteUserDialog(user: UserDetail) {
    const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("organiser.list.delete.dialog.title");
    modalRef.componentInstance.text = this.translocoService.translate("organiser.list.delete.dialog.text");

    modalRef.result.then((_) => {
      this.onDeleteUser(user);
    }, (_) => {
      /// ignore
    });
  }

  onDeleteUser(user: UserDetail): void {
    this.loaderService.startLoading();
    this.organiserUserService.deleteUser(user.userId!, this.currentOrganiser!.id!).subscribe((_: boolean):void => {
      this.organiserUserService.findUsers(this.currentOrganiser?.id).subscribe((users: UserDetail[]) => {
        this.users = users;
        this.onUsersChange.emit(users);
        this.loaderService.dismissLoading();
      });
    })
  }
  getFileFromServer(type: string) {
    this.exportService.downloadUsersByRole(type, this.createFilter()).subscribe(
      (blob: Blob) => {
        const formattedDateTime: string = this.getCurrentDateTimeFormatted();
        const filename: string = type + '_' + formattedDateTime + '.' + type;
        const downloadLink: HTMLAnchorElement = this.document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(blob);
        downloadLink.setAttribute('download', filename);
        this.document.body.appendChild(downloadLink);
        downloadLink.click();
      },
      error => {
        console.error('Error downloading file:', error);
      }
    );
  }

  getCurrentDateTimeFormatted(): string {
    const currentDateTime = new Date();
    const year: number = currentDateTime.getFullYear();
    const month: string = this.padNumber(currentDateTime.getMonth() + 1, 2);
    const day: string = this.padNumber(currentDateTime.getDate(), 2);
    return `${year}-${month}-${day}`;
  }

  padNumber(num: number, size: number): string {
    let numStr: string = num.toString();
    while (numStr.length < size) {
      numStr = '0' + numStr;
    }
    return numStr;
  }

  private createFilter() {
    return FindUsersDetailsRequest.createUsersDetailsRequest(
      this.page,
      {
        text: this.searchText !== "" ? this.searchText : undefined,
        role: this.selectedRole ?? undefined
      });
  }

  protected readonly ORGANIZER_ROLE: string = ORGANIZER_ROLE;
  protected readonly HELPER_ROLE: string = HELPER_ROLE;
  protected readonly USER_ROLE: string = USER_ROLE;
}


